import { yupResolver } from '@hookform/resolvers/yup'
import { EWorksheetDecision, getEWorksheetDecisionList } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Alert, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material'
import { memo, useEffect, useMemo, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ActionsDialog, InputTextField, SelectComponent } from '../../../../components'
import { CreditApplication, NormsMessage, WorksheetDecision, worksheetDecisionSchema } from '../../../../data/types'
import { IFinanceBaseWorksheet } from '../../../../data/types/IFinanceBaseWorksheetSchema'

interface Props {
  title: string
  onConfirm: (data: WorksheetDecision) => void
  onCancel: () => void
  open: boolean
  defaultValue: WorksheetDecision
  worksheetWarnings: NormsMessage[] | null
  worksheet: IFinanceBaseWorksheet
  creditapplication: CreditApplication
}
const WorksheetDecisionDialog = ({
  onConfirm,
  onCancel,
  title,
  open,
  defaultValue,
  worksheetWarnings,
  worksheet,
  creditapplication,
}: Props) => {
  const { register, handleSubmit, reset, watch } = useForm<WorksheetDecision>({
    mode: 'onBlur',
    defaultValues: defaultValue,
    // Forcing type to be backward compatible with the old schema (should be removed in the future)
    resolver: yupResolver(worksheetDecisionSchema) as unknown as Resolver<
      WorksheetDecision,
      unknown,
      WorksheetDecision
    >,
  })
  const { t } = useTranslation()
  const [acceptWarningMessage, setAcceptWarningsMessage] = useState<boolean>(false)

  useEffect(() => {
    reset(defaultValue)
  }, [defaultValue, reset])
  const [resetList, setReset] = useState<boolean>(false)

  const decision = watch('decision')

  const canApprove = defaultValue.cantApproveReasonCodes.length === 0
  const showCheckBox = worksheetWarnings != null && worksheetWarnings.length > 0

  const disableConfirmationButton = () => {
    if (decision === EWorksheetDecision.Approved) {
      if (!canApprove) return true
      if (showCheckBox && !acceptWarningMessage) return true
    }
    return false
  }

  const eWorkSheetDecision = useMemo(
    () =>
      getEWorksheetDecisionList()
        .filter((x) => x !== EWorksheetDecision.Pending && x !== EWorksheetDecision.Declined)
        .map((x) => ({ label: t(`EWorksheetDecision.${x}`, { ns: 'enum' }), value: x })),
    [t],
  )

  const maxPayment = useMemo(() => {
    return [
      { value: 0, label: t('editDecisionDialog.valueCannotChange') },
      { value: 10, label: t('editDecisionDialog.maxPaymentAddition', '', { value: '10' }) },
      { value: 20, label: t('editDecisionDialog.maxPaymentAddition', '', { value: '20' }) },
    ]
  }, [t])

  const maxTerm = useMemo(() => {
    return [
      { value: 0, label: t('editDecisionDialog.valueCannotChange') },
      { value: 6, label: t('editDecisionDialog.termAddition', '', { value: '6' }) },
      { value: 12, label: t('editDecisionDialog.termAddition', '', { value: '12' }) },
    ]
  }, [t])

  return (
    <ActionsDialog
      disabled={disableConfirmationButton()}
      title={title}
      open={open}
      onCancel={() => {
        reset(defaultValue)
        setReset(!resetList)
        onCancel()
      }}
      onConfirm={handleSubmit(onConfirm)}
    >
      <Grid container marginTop={2} spacing={2}>
        <Grid size={12}>
          <SelectComponent items={eWorkSheetDecision} label={t('worksheet.decision')} {...register('decision')} />
        </Grid>

        {(decision === EWorksheetDecision.Approved || decision === EWorksheetDecision.ApprovedWithCondition) &&
          !canApprove && (
            <Grid sx={{ mt: 1 }} size={12}>
              <div>
                {defaultValue?.cantApproveReasonCodes.map((error) => (
                  <Alert severity="error" key={error}>
                    {t(`EFinancingRefusalMessage.${error}`, { ns: 'enum' })}
                  </Alert>
                ))}
              </div>
            </Grid>
          )}
        {decision === EWorksheetDecision.Approved && canApprove && showCheckBox && (
          <Grid sx={{ mt: 1 }} size={12}>
            <div>
              {worksheetWarnings.map((message) => (
                <Alert severity="warning" key={message.id}>
                  {message.message}
                </Alert>
              ))}
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptWarningMessage}
                  onChange={(event) => setAcceptWarningsMessage(event.target.checked)}
                />
              }
              label={t('editDecisionDialog.acceptWarningMessage')}
            />
          </Grid>
        )}
        {decision === EWorksheetDecision.ApprovedWithCondition && canApprove && (
          <Grid container sx={{ mt: 1 }} size={12}>
            <Grid sx={{ mt: 1, pr: 1 }} size={6}>
              <SelectComponent
                items={maxTerm}
                label={t('editDecisionDialog.maxTermDuration')}
                {...register('maxTermDuration')}
              />
            </Grid>
            <Grid sx={{ mt: 1 }} size={6}>
              <SelectComponent
                items={maxPayment}
                label={t('editDecisionDialog.maxPmtAmount')}
                {...register('maxPmtAmount')}
              />
            </Grid>
            <Grid sx={{ mt: 1 }} size={12}>
              <InputTextField
                label={t('editDecisionDialog.maxTotalAmountFinanced')}
                {...register('maxTotalAmountFinanced')}
              />
            </Grid>
            <Grid sx={{ mt: 1 }} size={12}>
              <Typography variant="h6">{t('ECreditApplicationStep.financing', { ns: 'enum' })}</Typography>
            </Grid>
            <Grid sx={{ mt: 1 }} size={3}>
              <Typography variant="body2">
                {t('editDecisionDialog.maxTermDuration')}: {worksheet?.term} {t('worksheet.month')}
              </Typography>
            </Grid>
            <Grid sx={{ mt: 1 }} size={3}>
              <Typography variant="body2">
                {t('worksheet.totalAmountFinanced')}: {formatCurrency(worksheet?.totalAmountFinanced)}
              </Typography>
            </Grid>
            <Grid sx={{ mt: 1 }} size={3}>
              <Typography variant="body2">
                {t('credit.maxPayment')}: {formatCurrency(creditapplication.finalCreditDecision.maxPmtAmount)}{' '}
              </Typography>
            </Grid>
            <Grid sx={{ mt: 1 }} size={3}>
              <Typography variant="body2">
                {t('worksheet.paymentAmount')}: {formatCurrency(worksheet?.paymentAmountForFrequency)}{' '}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </ActionsDialog>
  )
}

export default memo(WorksheetDecisionDialog)
